import React, { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { useVenue } from "../VenueProvider";
import MetodoPagoField from "../shared/MetodoPagoField";
import { useAuth } from "../AuthProvider";
import { esSoloLectura } from "../../utils/roles";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import { fmtMXNInput, normalizeMoneyInput } from "../../utils/money";
import { CATEGORIAS_COSTO } from "../../utils/costos";
import { todayInputValue, parseDateInputToTimestamp, formatCalendarDate } from "../../utils/dates";
import {
  PERIODICIDADES,
  periodicidadLabel,
  propuestasPendientes,
  proximaOcurrencia,
  crearRecurrente,
  actualizarRecurrente,
  archivarRecurrente,
  setRecurrenteActivo,
  confirmarOcurrencia,
  saltarOcurrencia,
  costosDeRecurrente,
  estaActivo,
  cambioDePrograma,
} from "../../utils/recurrentes";
import HistorialPrecios from "../shared/HistorialPrecios";
import { Badge } from "../badge";
import { ACCENT, MONTHS_ES, formatMXN } from "./shared";
import { PlusIcon, XMarkIcon, ArrowPathIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

const inputClass =
  "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

const FORM_VACIO = {
  nombre: "",
  categoria: "Renta",
  monto: "",
  periodicidad: "mensual",
  inicio: todayInputValue(),
  proveedorId: "",
  notas: "",
};

const formDesde = (r) => ({
  nombre: r.nombre || "",
  categoria: r.categoria || "Otro",
  monto: r.monto != null ? String(r.monto) : "",
  periodicidad: r.periodicidad || "mensual",
  inicio: r.inicio?.toDate
    ? `${r.inicio.toDate().getFullYear()}-${String(r.inicio.toDate().getMonth() + 1).padStart(2, "0")}-${String(r.inicio.toDate().getDate()).padStart(2, "0")}`
    : todayInputValue(),
  proveedorId: r.proveedorId || "",
  notas: r.notas || "",
});

// Gastos recurrentes del venue (FRA-356). Dos piezas: las PROPUESTAS que
// tocan confirmar (banner, arriba de todo en Balance general) y el catálogo
// de recurrentes con su historial de precio. Una propuesta sin confirmar no
// toca el neto: solo al confirmarla se vuelve un costo con fecha de pago.
// Las propuestas se leen por mes: un encabezado con su subtotal y, dentro,
// por concepto (las dos quincenas de jardinería quedan juntas). Bajo el
// encabezado del mes, repetir "Septiembre 2026" en cada renglón es ruido;
// solo se dice lo que el mes no dice (la quincena, la semana, la
// periodicidad cuando no es mensual).
function agruparPorMes(propuestas, timeZone) {
  const grupos = new Map();
  propuestas.forEach((p) => {
    const dt = DateTime.fromJSDate(p.ocurrencia.fecha).setZone(timeZone || "America/Mexico_City");
    const key = dt.toFormat("yyyy-MM");
    if (!grupos.has(key)) {
      grupos.set(key, { key, label: `${MONTHS_ES[dt.month - 1]} ${dt.year}`, items: [], total: 0 });
    }
    const g = grupos.get(key);
    g.items.push(p);
    g.total += Number(p.recurrente.monto) || 0;
  });
  return [...grupos.values()]
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((g) => ({
      ...g,
      items: g.items.sort(
        (a, b) =>
          (a.recurrente.nombre || "").localeCompare(b.recurrente.nombre || "", "es") ||
          a.ocurrencia.fecha - b.ocurrencia.fecha
      ),
    }));
}

function detalleOcurrencia(p, timeZone) {
  const periodicidad = p.recurrente.periodicidad || "mensual";
  if (periodicidad === "mensual") return null;
  if (periodicidad === "quincenal") return p.ocurrencia.key.endsWith("Q1") ? "1ª quincena" : "2ª quincena";
  if (periodicidad === "semanal") {
    const dt = DateTime.fromJSDate(p.ocurrencia.fecha).setZone(timeZone || "America/Mexico_City");
    return `Semana del ${dt.day}`;
  }
  return periodicidadLabel(periodicidad);
}

export function PropuestasRecurrentes() {
  const { selectedVenueId, gastosRecurrentes, costos, calendarTimezone, metodosPagoConDefaults: metodosPago } = useVenue();
  const { user, userInfo } = useAuth();
  // Inversionista (P-FRA-13): ve lo que está por confirmarse, sin confirmar
  // ni saltar.
  const soloLectura = esSoloLectura(userInfo);
  const [confirmando, setConfirmando] = useState(null); // { key, monto, estado, fechaPago }
  const [saving, setSaving] = useState(false);

  const propuestas = useMemo(
    () => propuestasPendientes({ recurrentes: gastosRecurrentes, costos, timeZone: calendarTimezone }),
    [gastosRecurrentes, costos, calendarTimezone]
  );
  const grupos = useMemo(() => agruparPorMes(propuestas, calendarTimezone), [propuestas, calendarTimezone]);
  if (propuestas.length === 0) return null;
  const total = propuestas.reduce((s, p) => s + (Number(p.recurrente.monto) || 0), 0);

  const claveDe = (p) => `${p.recurrente.id}:${p.ocurrencia.key}`;

  const confirmar = async (p, ajuste) => {
    setSaving(true);
    try {
      const fechaPago = ajuste.estado === "pagado"
        ? parseDateInputToTimestamp(ajuste.fechaPago, calendarTimezone)
        : null;
      if (ajuste.estado === "pagado" && !fechaPago) {
        showAlert("Fecha de pago inválida.", "error");
        return;
      }
      const { costoId, monto } = await confirmarOcurrencia({
        venueId: selectedVenueId,
        recurrente: p.recurrente,
        ocurrencia: p.ocurrencia,
        monto: ajuste.monto,
        estado: ajuste.estado,
        fechaPago,
        metodo: ajuste.metodo || null,
        user,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.RECURRENTE_CONFIRMAR,
          entity: "recurrente",
          entityId: p.recurrente.id,
          entityName: p.recurrente.nombre || "",
          details: { periodo: p.ocurrencia.key, monto, estado: ajuste.estado, costoId },
        },
        userInfo,
        user
      );
      setConfirmando(null);
      showAlert(`${p.recurrente.nombre} · ${p.ocurrencia.label} registrado`, "success");
    } catch (err) {
      showAlert(err.message || "No se pudo confirmar", "error");
    } finally {
      setSaving(false);
    }
  };

  const saltar = async (p) => {
    try {
      await saltarOcurrencia({ venueId: selectedVenueId, recurrenteId: p.recurrente.id, periodoKey: p.ocurrencia.key, user });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.RECURRENTE_SALTAR,
          entity: "recurrente",
          entityId: p.recurrente.id,
          entityName: p.recurrente.nombre || "",
          details: { periodo: p.ocurrencia.key },
        },
        userInfo,
        user
      );
      showAlert("Ocurrencia saltada", "success");
    } catch (err) {
      showAlert(err.message || "No se pudo saltar", "error");
    }
  };

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 dark:bg-indigo-500/10 dark:border-indigo-500/20 p-4 mb-5">
      <div className="flex items-center gap-2 mb-2">
        <ArrowPathIcon className="h-4 w-4 text-indigo-600" />
        <div className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
          {propuestas.length === 1 ? "Un gasto recurrente por confirmar" : `${propuestas.length} gastos recurrentes por confirmar`}
          <span className="ml-2 font-normal text-indigo-700/80">{formatMXN(total)}</span>
        </div>
      </div>
      <p className="text-xs text-indigo-800/80 dark:text-indigo-300/80 mb-3">
        Se proponen cuando toca; mientras no los confirmes no cuentan en el neto ni en el flujo de caja. Puedes ajustar el monto o saltar una ocurrencia sin tocar el concepto.
      </p>
      <div className="space-y-4">
        {grupos.map((g) => (
          <div key={g.key}>
            <div className="flex items-baseline justify-between border-b border-indigo-200 dark:border-indigo-500/30 pb-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-indigo-900 dark:text-indigo-200">{g.label}</div>
              <div className="text-xs font-medium text-indigo-700/80 dark:text-indigo-300/80">{formatMXN(g.total)}</div>
            </div>
            <ul className="divide-y divide-indigo-100 dark:divide-indigo-500/20">
              {g.items.map((p) => {
                const key = claveDe(p);
                const abierto = confirmando?.key === key;
                const detalle = detalleOcurrencia(p, calendarTimezone);
                return (
                  <li key={key} className="py-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-900 dark:text-white">{p.recurrente.nombre}</div>
                        <div className="text-xs text-zinc-500">
                          {detalle ? `${detalle} · ` : ""}{p.recurrente.categoria || "Otro"}
                          {p.recurrente.proveedorNombre ? ` · ${p.recurrente.proveedorNombre}` : ""}
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-zinc-900 dark:text-white whitespace-nowrap">
                        {formatMXN(p.recurrente.monto)}
                      </div>
                      {!abierto && !soloLectura && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setConfirmando({ key, monto: String(p.recurrente.monto || ""), estado: "pagado", fechaPago: todayInputValue(), metodo: "" })}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm hover:opacity-90"
                            style={{ backgroundColor: ACCENT }}
                          >
                            Confirmar
                          </button>
                          <button type="button" onClick={() => saltar(p)} className="text-xs text-zinc-500 hover:text-zinc-700">
                            Saltar
                          </button>
                        </div>
                      )}
                    </div>
                    {abierto && (
                      <div className="mt-2 flex flex-wrap items-end gap-3 rounded-lg bg-white/70 dark:bg-zinc-900/40 p-3">
                        <div>
                          <label className="block text-xs font-medium text-zinc-500 mb-1">Monto</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={fmtMXNInput(confirmando.monto)}
                              onChange={(e) => setConfirmando({ ...confirmando, monto: normalizeMoneyInput(e.target.value) })}
                              className={`${inputClass} pl-7 w-36`}
                            />
                          </div>
                        </div>
                        <div className="flex rounded-lg border border-zinc-300 dark:border-zinc-600 overflow-hidden">
                          {[{ id: "pagado", label: "Pagado" }, { id: "por_pagar", label: "Por pagar" }].map((e) => (
                            <button
                              key={e.id}
                              type="button"
                              onClick={() => setConfirmando({ ...confirmando, estado: e.id })}
                              className={`px-3 py-2 text-xs font-medium transition ${confirmando.estado === e.id ? "text-white" : "bg-white dark:bg-zinc-800 text-zinc-500"}`}
                              style={confirmando.estado === e.id ? { backgroundColor: ACCENT } : undefined}
                            >
                              {e.label}
                            </button>
                          ))}
                        </div>
                        {confirmando.estado === "pagado" && (
                          <>
                            <div>
                              <label className="block text-xs font-medium text-zinc-500 mb-1">Fecha de pago</label>
                              <input type="date" value={confirmando.fechaPago} onChange={(e) => setConfirmando({ ...confirmando, fechaPago: e.target.value })} className={inputClass} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-zinc-500 mb-1">Método</label>
                              <MetodoPagoField
                                value={confirmando.metodo}
                                onChange={(v) => setConfirmando({ ...confirmando, metodo: v })}
                                metodosPago={metodosPago}
                                className={inputClass}
                              />
                            </div>
                          </>
                        )}
                        <button type="button" disabled={saving} onClick={() => confirmar(p, confirmando)} className="px-3 py-2 rounded-lg text-xs font-semibold text-white shadow-sm disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                          {saving ? "Guardando…" : "Registrar gasto"}
                        </button>
                        <button type="button" onClick={() => setConfirmando(null)} className="text-xs text-zinc-500 hover:text-zinc-700">Cancelar</button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// Catálogo de gastos recurrentes: alta, edición (con historial del monto),
// pausa y archivo. Colapsable dentro de Balance general.
export default function CuentasRecurrentes() {
  const { selectedVenueId, gastosRecurrentes, costos, proveedores, calendarTimezone } = useVenue();
  const { user, userInfo } = useAuth();
  // Inversionista (P-FRA-13): consulta conceptos e historial, sin editarlos.
  const soloLectura = esSoloLectura(userInfo);
  const [abierto, setAbierto] = useState(false);
  const [editando, setEditando] = useState(null); // null | "nuevo" | recurrente
  const [form, setForm] = useState(FORM_VACIO);
  const [notaCambio, setNotaCambio] = useState("");
  const [saving, setSaving] = useState(false);
  const [detalle, setDetalle] = useState(null); // id abierto

  const proveedoresPorId = useMemo(() => {
    const m = {};
    (proveedores || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [proveedores]);

  // Cuántas ocurrencias ya confirmadas quedarían selladas si se guarda este
  // cambio de calendario. 0 (o null) = no hay nada que advertir.
  const avisoDePrograma = useMemo(() => {
    if (!editando || editando === "nuevo") return 0;
    const inicio = form.inicio
      ? parseDateInputToTimestamp(form.inicio, calendarTimezone)
      : null;
    if (!cambioDePrograma(editando, { periodicidad: form.periodicidad, inicio })) return 0;
    return costosDeRecurrente(costos, editando.id).length;
  }, [editando, form.periodicidad, form.inicio, calendarTimezone, costos]);
  const lista = useMemo(
    () =>
      (gastosRecurrentes || [])
        .filter((r) => !r.archivedAt)
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es")),
    [gastosRecurrentes]
  );
  const mensualizado = lista.filter(estaActivo).reduce((s, r) => {
    const m = Number(r.monto) || 0;
    const f = { semanal: 52 / 12, quincenal: 2, mensual: 1, bimestral: 1 / 2, trimestral: 1 / 3, semestral: 1 / 6, anual: 1 / 12 }[r.periodicidad] || 1;
    return s + m * f;
  }, 0);

  const abrirNuevo = () => { setForm(FORM_VACIO); setNotaCambio(""); setEditando("nuevo"); setAbierto(true); };
  const abrirEdicion = (r) => { setForm(formDesde(r)); setNotaCambio(""); setEditando(r); };

  const guardar = async () => {
    setSaving(true);
    try {
      const datos = { ...form, inicio: form.inicio ? parseDateInputToTimestamp(form.inicio, calendarTimezone) : null };
      if (editando === "nuevo") {
        const id = await crearRecurrente({ venueId: selectedVenueId, datos, user, userInfo, proveedoresPorId });
        logBitacora(selectedVenueId, { action: BITACORA_ACTIONS.RECURRENTE_CREATE, entity: "recurrente", entityId: id, entityName: form.nombre, details: { monto: Number(normalizeMoneyInput(form.monto)) || 0, periodicidad: form.periodicidad } }, userInfo, user);
        showAlert("Gasto recurrente creado", "success");
      } else {
        await actualizarRecurrente({ venueId: selectedVenueId, recurrente: editando, datos, notas: notaCambio.trim() || null, user, userInfo, proveedoresPorId, costos });
        logBitacora(selectedVenueId, { action: BITACORA_ACTIONS.RECURRENTE_EDIT, entity: "recurrente", entityId: editando.id, entityName: form.nombre, details: { monto: Number(normalizeMoneyInput(form.monto)) || 0, montoAnterior: editando.monto, periodicidad: form.periodicidad } }, userInfo, user);
        showAlert("Gasto recurrente actualizado", "success");
      }
      setEditando(null);
    } catch (err) {
      showAlert(err.message || "No se pudo guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  const archivar = async (r) => {
    try {
      await archivarRecurrente({ venueId: selectedVenueId, recurrenteId: r.id, user });
      logBitacora(selectedVenueId, { action: BITACORA_ACTIONS.RECURRENTE_ARCHIVE, entity: "recurrente", entityId: r.id, entityName: r.nombre || "", details: {} }, userInfo, user);
      showAlert("Gasto recurrente archivado", "success");
      if (editando?.id === r.id) setEditando(null);
    } catch (err) {
      showAlert(err.message || "No se pudo archivar", "error");
    }
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 mb-5">
      <button type="button" onClick={() => setAbierto((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left">
        <ChevronRightIcon className={`h-4 w-4 text-zinc-400 transition-transform ${abierto ? "rotate-90" : ""}`} />
        <span className="text-xs uppercase tracking-wide font-semibold text-zinc-500">Gastos recurrentes</span>
        <span className="text-xs text-zinc-400">
          {lista.length === 0 ? "ninguno configurado" : `${lista.length} · ≈ ${formatMXN(Math.round(mensualizado))} al mes`}
        </span>
        {!soloLectura && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); abrirNuevo(); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); abrirNuevo(); } }}
          className="ml-auto inline-flex items-center gap-1 text-xs font-semibold hover:underline"
          style={{ color: ACCENT }}
        >
          <PlusIcon className="h-3.5 w-3.5" /> Nuevo
        </span>
        )}
      </button>

      {abierto && (
        <div className="border-t border-zinc-100 dark:border-zinc-700/40 p-4 space-y-3">
          <p className="text-xs text-zinc-500">
            Limpieza, fumigación, renta: gastos que se repiten y que no cuelgan de ningún evento. El sistema los propone cuando toca; tú confirmas.
          </p>

          {editando && !soloLectura && (
            <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-600 p-3 space-y-3">
              <div className="flex items-center">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{editando === "nuevo" ? "Nuevo gasto recurrente" : `Editar ${editando.nombre}`}</div>
                <button type="button" onClick={() => setEditando(null)} className="ml-auto text-zinc-400 hover:text-zinc-600"><XMarkIcon className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="lg:col-span-2">
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Nombre</label>
                  <input type="text" value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} placeholder="Limpieza general, Renta, Fumigación…" className={inputClass} autoFocus />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Monto</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
                    <input type="text" inputMode="decimal" value={fmtMXNInput(form.monto)} onChange={(e) => set({ monto: normalizeMoneyInput(e.target.value) })} className={`${inputClass} pl-7`} placeholder="0" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Cada</label>
                  <select value={form.periodicidad} onChange={(e) => set({ periodicidad: e.target.value })} className={inputClass}>
                    {PERIODICIDADES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Desde</label>
                  <input type="date" value={form.inicio} onChange={(e) => set({ inicio: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Categoría</label>
                  <select value={form.categoria} onChange={(e) => set({ categoria: e.target.value })} className={inputClass}>
                    {CATEGORIAS_COSTO.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Proveedor (opcional)</label>
                  <select value={form.proveedorId} onChange={(e) => set({ proveedorId: e.target.value })} className={inputClass}>
                    <option value="">Ninguno</option>
                    {(proveedores || []).filter((p) => !p.archivedAt).map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Notas</label>
                  <input type="text" value={form.notas} onChange={(e) => set({ notas: e.target.value })} className={inputClass} />
                </div>
                {editando !== "nuevo" && String(editando.monto) !== normalizeMoneyInput(form.monto) && (
                  <div className="sm:col-span-2 lg:col-span-4">
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Nota del cambio de monto (opcional)</label>
                    <input type="text" value={notaCambio} onChange={(e) => setNotaCambio(e.target.value)} placeholder="Subió la renta en marzo" className={inputClass} />
                  </div>
                )}
                {/* Cambiar cada cuándo se repite (o desde cuándo) rehace el
                    calendario entero: las ocurrencias pasan a llamarse de
                    otra forma. Lo ya confirmado queda sellado para que no se
                    vuelva a proponer, y eso hay que decirlo antes, no
                    después. */}
                {avisoDePrograma && (
                  <div className="sm:col-span-2 lg:col-span-4 rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
                    Vas a cambiar cada cuándo se repite. Las {avisoDePrograma} ocurrencias
                    que ya confirmaste se conservan y no se volverán a proponer; el
                    calendario nuevo empieza después de la última.
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={guardar} disabled={saving} className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                  {saving ? "Guardando…" : editando === "nuevo" ? "Crear" : "Guardar cambios"}
                </button>
                <button type="button" onClick={() => setEditando(null)} className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700">Cancelar</button>
                {editando !== "nuevo" && (
                  <button type="button" onClick={() => archivar(editando)} className="ml-auto text-xs text-red-500 hover:text-red-700">Archivar</button>
                )}
              </div>
            </div>
          )}

          {lista.length === 0 ? (
            <p className="text-sm text-zinc-400 italic">Todavía no hay gastos recurrentes.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60">
              <table className="min-w-full text-sm">
                <thead className="bg-zinc-50 dark:bg-zinc-800/40">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Gasto</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Cada</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Monto</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Próximo</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {lista.map((r) => {
                    const prox = estaActivo(r) ? proximaOcurrencia(r, { timeZone: calendarTimezone }) : null;
                    const abiertoDetalle = detalle === r.id;
                    const historialCostos = costosDeRecurrente(costos, r.id);
                    return (
                      <React.Fragment key={r.id}>
                        <tr>
                          <td className="px-3 py-2">
                            <button type="button" onClick={() => setDetalle(abiertoDetalle ? null : r.id)} className="text-left font-medium text-zinc-900 dark:text-white hover:underline">
                              {r.nombre}
                            </button>
                            <div className="text-xs text-zinc-400">{r.categoria || "Otro"}{r.proveedorNombre ? ` · ${r.proveedorNombre}` : ""}</div>
                          </td>
                          <td className="px-3 py-2 text-zinc-500">{periodicidadLabel(r.periodicidad)}</td>
                          <td className="px-3 py-2 text-right font-medium text-zinc-900 dark:text-white whitespace-nowrap">{formatMXN(r.monto)}</td>
                          <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">
                            {!estaActivo(r) ? <Badge color="zinc">En pausa</Badge> : prox ? prox.label : "—"}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {!soloLectura && (
                            <>
                            <button type="button" onClick={() => abrirEdicion(r)} className="text-xs text-zinc-500 hover:text-zinc-800 mr-3">Editar</button>
                            <button
                              type="button"
                              onClick={() => setRecurrenteActivo({ venueId: selectedVenueId, recurrenteId: r.id, activo: !estaActivo(r), user })}
                              className="text-xs text-zinc-500 hover:text-zinc-800"
                            >
                              {estaActivo(r) ? "Pausar" : "Reanudar"}
                            </button>
                            </>
                            )}
                          </td>
                        </tr>
                        {abiertoDetalle && (
                          <tr className="bg-zinc-50/60 dark:bg-zinc-800/30">
                            <td colSpan={5} className="px-3 py-3">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <HistorialPrecios
                                  pathSegments={["venues", selectedVenueId, "gastosRecurrentes", r.id, "costoHistorial"]}
                                  titulo="Historial del monto"
                                  labels={{ inicial: "Monto inicial", eliminado: "Sin monto" }}
                                />
                                <div className="rounded-xl border border-zinc-100 bg-white dark:bg-zinc-800/30 dark:border-zinc-700/40 p-5">
                                  <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500 mb-3">Ocurrencias registradas</div>
                                  {historialCostos.length === 0 ? (
                                    <p className="text-sm text-zinc-400">Ninguna todavía.</p>
                                  ) : (
                                    <ul className="text-sm divide-y divide-zinc-100 dark:divide-zinc-700/40">
                                      {historialCostos.slice(0, 12).map((c) => (
                                        <li key={c.id} className="flex items-center justify-between py-1.5">
                                          <span className="text-zinc-700 dark:text-zinc-300">{c.periodo}</span>
                                          <span className="text-xs text-zinc-400">{c.estado === "pagado" ? formatCalendarDate(c.fechaPago, calendarTimezone, { style: "short" }) : "por pagar"}</span>
                                          <span className="font-medium text-zinc-900 dark:text-white">{formatMXN(c.monto)}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {(Array.isArray(r.saltados) ? r.saltados : []).length > 0 && (
                                    <p className="mt-2 text-xs text-zinc-400">Saltadas: {r.saltados.join(", ")}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
